import { type FC } from 'react';
import type { EvalConfigVersionSummary } from '@queries/evalConfigs';
import { formatDate } from '@root/helpers';

import { cardBodyClasses, cardClasses, cardHeaderClasses, sectionHeadingClasses, subtleTextClasses } from '../classes';

interface VersionsTabProps {
  currentVersionNumber?: number;
  hasUnpublishedChanges: boolean;
  versions: EvalConfigVersionSummary[];
}

export const VersionsTab: FC<VersionsTabProps> = ({ currentVersionNumber, hasUnpublishedChanges, versions }) => (
  <div className={cardClasses}>
    <div className={cardHeaderClasses}>
      <div className="flex flex-col">
        <h2 className={sectionHeadingClasses}>Versions</h2>
        <p className={subtleTextClasses}>
          Published draft snapshots. {hasUnpublishedChanges ? 'The draft has unsaved changes not reflected here.' : ''}
        </p>
      </div>
    </div>
    <div className={cardBodyClasses}>
      {versions.length === 0 && <p className={subtleTextClasses}>No versions published yet.</p>}
      {versions.length > 0 && (
        <table className="w-full text-sm" data-testid="table:versions">
          <thead>
            <tr className="text-info-500 border-info-100 border-b text-left">
              <th className="py-2 font-medium">Version</th>
              <th className="py-2 font-medium">Published at</th>
              <th className="py-2 font-medium">Published by</th>
              <th className="py-2 font-medium">Note</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => {
              const isCurrent = v.version_number === currentVersionNumber;
              return (
                <tr
                  key={v.version_number}
                  className="border-info-100 border-b last:border-b-0"
                  data-testid={`row:version-${v.version_number}`}
                >
                  <td className="text-info-900 py-2 font-medium">
                    v{v.version_number}
                    {isCurrent && (
                      <span className="border-success-200 bg-success-50 text-success-700 ml-2 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium">
                        Current
                      </span>
                    )}
                  </td>
                  <td className="text-info-700 py-2">{formatDate(v.published_at, 'DD MMM YYYY HH:mm')}</td>
                  <td className="text-info-700 py-2">{v.published_by?.name ?? '—'}</td>
                  <td className="text-info-500 py-2">{v.change_note ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  </div>
);
